import React from 'react';
import PropTypes from 'prop-types';
import { EventEmitter } from 'events';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { branch, lifecycle, renderNothing, withProps, withState, compose, setPropTypes, withHandlers } from 'recompose';

import CancelButton from '../../../common/buttons/cancelButton';
import SaveButton from '../../../common/buttons/saveButton';
import withToggle from '../../../common/hoc/withToggle';
import Input from '../../../common/input';
import Modal from '../../../common/Modal';
import Select from '../../../common/select';
import { trimFunction } from '../../../common/trimData';
import { timeTrackingBarberToggle } from '../actions';
import { getBarberActive, requirePasscodeEnabled } from '../selectors';
import { getBarberAllowedTimeTrackRoles } from '../../barbers/selector';
import { ChurnZeroService, CLOCK_IN, CLOCK_OUT } from '../../../../modules/infra/observability/churnzero';
import { getDefaultTrackingRole } from '../../constants';
import { Logger } from '../../../../modules/infra/observability/logger';
import { getActiveShopId } from '../../../auth/selectors';

const Form = ({ active, requirePasscode, handleSubmit, onCancel, allowedTimeTrackRoles, loadingIsOn }) => (
  <form className="layout" onSubmit={handleSubmit}>
    {!active && <div className="layout__item u-mb+">
      <span className="c-label-new">Clock In as</span>
      <Select
        name="role"
        className="c-select"
        options={allowedTimeTrackRoles}
      />
    </div>}
    {requirePasscode && <div className="layout__item u-mb+">
      <span className="c-label-new">Please enter your Passcode</span>
      <Input
        className="c-input c-masked-input"
        name="passcode"
        type="number"
        inputmode="tel"
        autocomplete="off"
        placeholder="0000"
      />
    </div>}

    <div className="layout__item u-text-right">
      <CancelButton content="Close" onClick={onCancel} disabled={loadingIsOn} />
      <SaveButton content={`Clock ${active ? 'Out' : 'In'}`} disabled={loadingIsOn} />
    </div>
  </form>
);

Form.propTypes = {
  active: PropTypes.bool.isRequired,
  requirePasscode: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loadingIsOn: PropTypes.bool.isRequired,
};

Form.defaultProps = {};

const TIME_TRACKING_FORM = 'TIME_TRACKING_FORM';

const TrackTimeForm = compose(
  setPropTypes({
    onSubmit: PropTypes.func.isRequired,
  }),
  withHandlers({
    onSubmit: props => trimFunction(props.onSubmit),
  }),
  withProps((props) => {
    const allowedRoleValues = (props.allowedTimeTrackRoles || []).map(item => item.value);
    return {
      initialValues: {
        role: getDefaultTrackingRole(allowedRoleValues),
      },
    };
  }),
  reduxForm({
    form: TIME_TRACKING_FORM,
    enableReinitialize: true,
  }),
)(Form);

const ExtendedClockInModal = ({
  active,
  onTimeTrackToggle,
  requirePasscode,
  closeModal,
  allowedTimeTrackRoles,
  loadingIsOn,
}) => (
  <Modal title={`Clock ${active ? 'Out' : 'In'}`} close={closeModal} showCloseButton>
    <div className="u-pb+ u-pt u-ph+">
      <TrackTimeForm
        active={active}
        requirePasscode={requirePasscode}
        onSubmit={onTimeTrackToggle}
        onCancel={closeModal}
        allowedTimeTrackRoles={allowedTimeTrackRoles}
        loadingIsOn={loadingIsOn}
      />
    </div>
  </Modal>
);

ExtendedClockInModal.propTypes = {
  active: PropTypes.bool.isRequired,
  requirePasscode: PropTypes.bool.isRequired,
  onTimeTrackToggle: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  allowedTimeTrackRoles: PropTypes.array.isRequired,
  loadingIsOn: PropTypes.bool.isRequired,
};


const mapStateToProps = (state, ownProps) => ({
  barberId: ownProps.barberId,
  shopId: getActiveShopId(state),
  active: getBarberActive(state, ownProps.barberId),
  requirePasscode: requirePasscodeEnabled(state),
  allowedTimeTrackRoles: getBarberAllowedTimeTrackRoles(state, ownProps.barberId),
});

const mapDispatchToProps = {
  timeTrackingBarberToggle,
};

export default compose(
  withToggle('modalVisible'),
  withToggle('loading'),
  withState('barberId', 'setBarberId'),
  withHandlers({
    setBusyOn: () => (barberId) => {
      EventEmitter.prototype.emit('extended-clock-in-busy', { barberId, busy: true });
    },
    setBusyOff: () => (barberId) => {
      EventEmitter.prototype.emit('extended-clock-in-busy', { barberId, busy: false });
    },
    openModal: props => (barberId) => {
      props.setBarberId(barberId);
      props.modalVisibleOn();
    },
    closeModal: props => () => {
      props.setBarberId(undefined);
      props.modalVisibleOff();
    },
  }),
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      EventEmitter.prototype.on('open-extended-clock-in-modal', this.props.openModal);
      EventEmitter.prototype.on('close-extended-clock-in-modal', this.props.closeModal);
    },
    componentWillUnmount() {
      EventEmitter.prototype.removeListener('open-extended-clock-in-modal', this.props.openModal);
      EventEmitter.prototype.removeListener('close-extended-clock-in-modal', this.props.closeModal);
    },
  }),
  withHandlers({
    onTimeTrackToggle: props => (data) => {
      if (!props.loadingIsOn) {
        props.setBusyOn(props.barberId);
        props.loadingOn();

        ChurnZeroService.track(props.active ? CLOCK_OUT : CLOCK_IN);

        props.timeTrackingBarberToggle(props.barberId, data)
          .then((result) => {
            // active state did not change after toggling, which must not happen
            if (props.active === result.active) {
              Logger.error('Barber time tracking activity state didn\'t change after toggling.');
              console.log('Details:', {
                ...result,
                shopId: props.shopId,
              });
            }

            props.closeModal();
          })
          .finally(() => {
            props.setBusyOff(props.barberId);
            props.loadingOff();
          });
      }
    },
  }),
  branch(
    props => !props.modalVisibleIsOn,
    renderNothing
  )
)(ExtendedClockInModal);
